import { useState, useCallback } from 'react';
import { getColorHex } from '../../../../hooks/useAdminClientePerfil';

const PALETA_PREDEFINIDA = [
  'Blanco', 'Negro', 'Azul marino', 'Gris perla', 'Beige', 'Azul eléctrico',
  'Burdeos', 'Tweed marrón', 'Crudo', 'Verde bosque', 'Terracota', 'Rosa pálido',
  'Oro', 'Plata', 'Cobre',
];

interface Props {
  paletteColors: string[];
  setPaletteColors: (v: string[] | ((prev: string[]) => string[])) => void;
}

export default function ColorPaletteSection({ paletteColors, setPaletteColors }: Props) {
  const [editingPalette, setEditingPalette] = useState(false);
  const [customColor, setCustomColor] = useState('');

  const toggleColor = useCallback((color: string) => {
    setPaletteColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  }, [setPaletteColors]);

  const addCustomColor = useCallback(() => {
    const trimmed = customColor.trim();
    if (!trimmed || paletteColors.includes(trimmed)) return;
    setPaletteColors((prev) => [...prev, trimmed]);
    setCustomColor('');
  }, [customColor, paletteColors, setPaletteColors]);

  return (
    <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-serif text-brand-black text-lg font-medium">Paleta de colores</h3>
          <p className="font-sans text-brand-gray-500 text-xs mt-0.5">
            Selecciona los colores que recomiendas para este cliente
          </p>
        </div>
        <button
          onClick={() => setEditingPalette((v) => !v)}
          className="inline-flex items-center gap-1.5 font-sans text-sm px-3 py-2 rounded-lg border border-brand-gray-200 hover:border-brand-black hover:text-brand-black transition-colors"
        >
          <i className={editingPalette ? 'ri-eye-line' : 'ri-pencil-line'} />
          {editingPalette ? 'Vista previa' : 'Editar'}
        </button>
      </div>

      {editingPalette ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {PALETA_PREDEFINIDA.map((color) => {
              const hex = getColorHex(color);
              const selected = paletteColors.includes(color);
              return (
                <button
                  key={color}
                  type="button"
                  onClick={() => toggleColor(color)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-sans text-xs transition-all ${
                    selected
                      ? 'border-brand-black ring-1 ring-brand-black'
                      : 'border-brand-gray-200 hover:border-brand-gray-400'
                  }`}
                >
                  <span
                    className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                    style={{ backgroundColor: hex }}
                  />
                  <span>{color}</span>
                  {selected && <i className="ri-check-line text-brand-black" />}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomColor(); } }}
              placeholder="Añadir color personalizado..."
              className="flex-1 border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
            />
            <button
              type="button"
              onClick={addCustomColor}
              className="inline-flex items-center gap-1 font-sans text-sm px-3 py-2 rounded-lg border border-brand-gray-200 hover:border-brand-black transition-colors"
            >
              <i className="ri-add-line" />
              Añadir
            </button>
          </div>
        </div>
      ) : (
        <div>
          {paletteColors.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {paletteColors.map((color) => {
                const hex = getColorHex(color);
                return (
                  <span
                    key={color}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-gray-200 font-sans text-xs"
                    style={{ backgroundColor: hex + '20' }}
                  >
                    <span
                      className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                      style={{ backgroundColor: hex }}
                    />
                    <span>{color}</span>
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-3 text-brand-gray-400">
              <i className="ri-palette-line text-lg" />
              <p className="font-sans text-sm">Aún no hay colores seleccionados</p>
            </div>
          )}
        </div>
      )}

      {editingPalette && paletteColors.length > 0 && (
        <div className="mt-4 pt-4 border-t border-brand-gray-200">
          <p className="font-sans text-brand-gray-500 text-xs mb-2">
            {paletteColors.length} color{paletteColors.length !== 1 ? 'es' : ''} en la paleta
          </p>
          <div className="flex flex-wrap gap-2">
            {paletteColors.map((color) => {
              const hex = getColorHex(color);
              return (
                <span
                  key={color}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-gray-200 font-sans text-xs bg-brand-gray-50"
                >
                  <span
                    className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                    style={{ backgroundColor: hex }}
                  />
                  {color}
                  <button
                    type="button"
                    onClick={() => toggleColor(color)}
                    className="w-4 h-4 flex items-center justify-center text-brand-gray-400 hover:text-red-500"
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}